import { useTheme } from 'react-admin';
import uPlot from 'uplot';

export interface PairPoint {
    x: number;
    y: number;
}

export interface IntegralPair {
    start: PairPoint;
    end?: PairPoint;
    sample_name?: string;
}

export interface IntegralResult {
    start: number;
    end: number;
    area: number | null;
    sample_name?: string;
}

// Stable identity for absent fields, so a render does not look like new data
export const EMPTY: never[] = [];

const traceColors = (isDark: boolean) => ({
    raw: isDark ? '#3987e5' : '#2a78d6',
    rawMuted: isDark ? '#8b8b8b' : '#9b9b9b',
    corrected: isDark ? '#12b3a5' : '#0f9186',
    spline: '#d55181',
    selected: isDark ? '#c98500' : '#eda100',
    // Tinted up for the dark background, where mid tones read as muddy
    fills: isDark
        ? ['#3fd9a0', '#b3a9f5', '#ff9c9c']
        : ['#1baf7a', '#4a3aa7', '#e34948'],
    ring: isDark ? '#121212' : '#ffffff',
});

export type TraceColors = ReturnType<typeof traceColors>;

export const withAlpha = (hex: string, alpha = 0.25) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export interface ChartTheme {
    isDark: boolean;
    colors: TraceColors;
    gridColor: string;
    fontColor: string;
    zeroLineColor: string;
    bandAlpha: number;
    // uPlot's drag-to-zoom rectangle defaults to a black wash
    selectColor: string;
}

export const useChartTheme = (): ChartTheme => {
    const [mode] = useTheme();
    const isDark = mode === 'dark';

    return {
        isDark,
        colors: traceColors(isDark),
        gridColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        fontColor: isDark ? '#ffffff' : '#000000',
        zeroLineColor: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)',
        bandAlpha: isDark ? 0.3 : 0.25,
        selectColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.07)',
    };
};

// Traces why a click on the plot did or did not place a point. Off unless the
// console has run: localStorage.sensormapPickDebug = '1'
export const pickTrace = (message: string, detail?: Record<string, unknown>) => {
    try {
        if (localStorage.getItem('sensormapPickDebug') !== '1') return;
    } catch {
        return;
    }
    // Flattened into the line so it can be copied out of the console as text
    const fields = detail
        ? Object.entries(detail).map(([key, value]) => ` ${key}=${value}`).join('')
        : '';
    console.log(`[pick] ${message}${fields}`);
};

// Channel currents sit around 1e-6 A, where fixed point reads as a row of zeroes
export const formatAmps = (value: number | null | undefined) =>
    value == null ? '' : value === 0 ? '0' : value.toExponential(2);

export const formatSeconds = (value: number | null | undefined) =>
    value == null ? '' : Number.isInteger(value) ? String(value) : value.toFixed(2);

export const sliceRange = (x: number[], x0: number, x1: number) => {
    const [lo, hi] = x0 <= x1 ? [x0, x1] : [x1, x0];
    let i0 = x.findIndex(v => v >= lo);
    if (i0 === -1) i0 = x.length - 1;
    let i1 = x.length - 1;
    while (i1 > i0 && x[i1] > hi) i1--;
    return [i0, i1];
};

export const pairName = (pair: IntegralPair, index: number) =>
    pair.sample_name || `Sample ${index + 1}`;

const BRACKET_HALF_HEIGHT = 11;
const BRACKET_TICK = 5;
const HOVER_RING_RADIUS = 10;

// Ring around the chosen point under the pointer, marking it as removable
export const makeHoverHook = (
    getHover: () => PairPoint | null,
    theme: ChartTheme,
) => (u: uPlot) => {
    const point = getHover();
    if (!point) return;

    const { ctx } = u;
    const dpr = uPlot.pxRatio || 1;
    const { left, top, width, height } = u.bbox;

    ctx.save();
    ctx.beginPath();
    ctx.rect(left, top, width, height);
    ctx.clip();
    ctx.beginPath();
    ctx.arc(
        u.valToPos(point.x, 'x', true),
        u.valToPos(point.y, 'y', true),
        HOVER_RING_RADIUS * dpr,
        0,
        Math.PI * 2,
    );
    ctx.strokeStyle = theme.colors.selected;
    ctx.lineWidth = 2 * dpr;
    ctx.stroke();
    ctx.restore();
};

// Reached through a ref: the hooks are baked into the options at construction
export interface BandSource {
    time: number[];
    corrected: number[];
    bands: IntegralPair[];
}

// Brackets and name for each range. Runs after the series so the marks are
// not painted over.
export const makeRangeHook = (
    getSource: () => BandSource,
    theme: ChartTheme,
) => (u: uPlot) => {
    const { bands } = getSource();
    if (!bands.length) return;

    const { ctx } = u;
    const dpr = uPlot.pxRatio || 1;
    const { left, top, width, height } = u.bbox;

    // Range series occupy the tail of the series list, so a range hidden from
    // the legend takes its brackets and name with it
    const active = bands.filter(band => band.end);
    const firstBandSeries = u.series.length - active.length;

    const half = BRACKET_HALF_HEIGHT * dpr;

    // Ticks point into the range: [ opens it, ] closes it
    const bracket = (point: PairPoint, facing: 1 | -1) => {
        const x = u.valToPos(point.x, 'x', true);
        const y = u.valToPos(point.y, 'y', true);
        const tick = BRACKET_TICK * dpr * facing;
        ctx.beginPath();
        ctx.moveTo(x, y - half);
        ctx.lineTo(x, y + half);
        ctx.moveTo(x, y - half);
        ctx.lineTo(x + tick, y - half);
        ctx.moveTo(x, y + half);
        ctx.lineTo(x + tick, y + half);
        ctx.stroke();
        return { x, y };
    };

    ctx.save();
    ctx.beginPath();
    ctx.rect(left, top, width, height);
    ctx.clip();
    ctx.font = `${11 * dpr}px system-ui`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'bottom';
    ctx.lineWidth = 2 * dpr;
    ctx.strokeStyle = theme.colors.selected;

    bands.forEach((band, index) => {
        const activeIndex = band.end ? active.indexOf(band) : -1;
        if (activeIndex !== -1 && !u.series[firstBandSeries + activeIndex]?.show) {
            return;
        }
        const start = bracket(band.start, 1);
        if (band.end) bracket(band.end, -1);
        ctx.fillStyle = theme.fontColor;
        ctx.fillText(pairName(band, index), start.x + 4 * dpr, start.y - half - 3 * dpr);
    });

    ctx.restore();
};

// Zero line the range shading reads against, drawn under the axes and series
export const makeBackgroundHook = (theme: ChartTheme) => (u: uPlot) => {
    const scale = u.scales.y;
    if (scale?.min == null || scale.max == null || scale.min > 0 || scale.max < 0) {
        return;
    }

    const { ctx } = u;
    const { left, top, width, height } = u.bbox;
    const yZero = u.valToPos(0, 'y', true);

    ctx.save();
    ctx.beginPath();
    ctx.rect(left, top, width, height);
    ctx.clip();
    ctx.beginPath();
    ctx.strokeStyle = theme.zeroLineColor;
    ctx.lineWidth = uPlot.pxRatio || 1;
    ctx.moveTo(left, yZero);
    ctx.lineTo(left + width, yZero);
    ctx.stroke();
    ctx.restore();
};

// One series per completed range, filled from the corrected curve down to
// zero: the same signed area the API integrates
export const bandSeries = (
    band: IntegralPair,
    index: number,
    theme: ChartTheme,
): uPlot.Series => {
    const color = theme.colors.fills[index % theme.colors.fills.length];
    return {
        label: pairName(band, index),
        stroke: color,
        width: 0,
        fill: withAlpha(color, theme.bandAlpha),
        fillTo: () => 0,
        spanGaps: false,
        // A slice of the corrected signal must not widen the y axis on its own
        auto: false,
        points: { show: false },
    };
};

// Corrected values inside the range, null outside
export const bandColumn = (
    band: IntegralPair,
    time: number[],
    corrected: number[],
) => {
    const column = new Array<number | null>(time.length).fill(null);
    if (!band.end || !corrected.length) return column;
    const [i0, i1] = sliceRange(time, band.start.x, band.end.x);
    for (let i = i0; i <= i1; i++) column[i] = corrected[i] ?? null;
    return column;
};
